/**
 * NetobsPacketLogPanel (NO-5, in-game) — scrollable inspector for the
 * last ~300 client-observed packets. Reads NetworkTracer's recentTraffic
 * ring + per-type stats Maps every 500ms.
 *
 * Lives in-game (alongside CombatLogPanel + AbilityLifecycleMonitor +
 * TargetLifecycleMonitor) — the right home for client-side observation,
 * since /diagnostic is a separate document with no live `window.nm`.
 *
 * Hosted in a ToolWindow (shared collapsible / draggable / persistent
 * substrate). Matches the AbilityLifecycleMonitor shape.
 *
 * History: NO-5 originally shipped as a DiagnosticHub tab
 * (NetobsPacketLogTab) but `/diagnostic` is a separate route that
 * tears down the game state, so the tab couldn't see `window.nm`.
 * Moved in-game 2026-05-03 — same data, different host.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { NETOBS_COLORS } from '../netobs/colors';
import { isPopoutMode } from '../../systems/netobs/popout/popoutMode';
import { popoutShim } from '../../systems/netobs/popout/popoutShimState';

interface TrafficEvent {
  seq: number;
  timestamp: string;
  direction: 'client->server' | 'server->client';
  type: string;
  bytes: number;
}

interface MessageTypeStats {
  count: number;
  totalBytes: number;
  avgBytes: number;
  lastAt: string | null;
}

type DirectionFilter = 'all' | 'in' | 'out';

const POLL_INTERVAL_MS = 500;
const MAX_VISIBLE_ROWS = 300;


function readTracer(): {
  events: TrafficEvent[];
  outboundStats: Map<string, MessageTypeStats>;
  inboundStats: Map<string, MessageTypeStats>;
} | null {
  // Popout mode: read from the BroadcastChannel-fed shim. Same hook
  // pattern as useNetobsTraffic — popout has no Colyseus connection
  // so window.nm is always null.
  if (isPopoutMode()) {
    const t = popoutShim.getTraffic();
    if (!t) return null;
    return {
      events: t.recentTraffic as TrafficEvent[],
      outboundStats: t.outboundStats as Map<string, MessageTypeStats>,
      inboundStats: t.inboundStats as Map<string, MessageTypeStats>,
    };
  }
  const tracer = (globalThis as any).nm?.tracer;
  if (!tracer || !Array.isArray(tracer.recentTraffic)) return null;
  return {
    events: tracer.recentTraffic as TrafficEvent[],
    outboundStats: tracer.outboundMessageTypeStats as Map<string, MessageTypeStats>,
    inboundStats: tracer.inboundMessageTypeStats as Map<string, MessageTypeStats>,
  };
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}K`;
  return `${(n / 1024 / 1024).toFixed(2)}M`;
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const ms = String(d.getMilliseconds()).padStart(3, '0');
  return `${hh}:${mm}:${ss}.${ms}`;
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  fontFamily: 'monospace',
  fontSize: 11,
  borderCollapse: 'collapse',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '4px 8px',
  borderBottom: '1px solid #2a2f3a',
  color: '#7af',
  position: 'sticky',
  top: 0,
  background: '#0d0f15',
};

const tdStyle: React.CSSProperties = {
  padding: '3px 8px',
  borderBottom: '1px solid #1a1d24',
};

const chipStyle = (active: boolean): React.CSSProperties => ({
  display: 'inline-block',
  padding: '4px 10px',
  marginRight: 6,
  background: active ? '#2a3548' : '#15171c',
  color: active ? '#fff' : '#888',
  border: `1px solid ${active ? '#4ea3ff' : '#2a2f3a'}`,
  borderRadius: 3,
  fontSize: 11,
  cursor: 'pointer',
  fontFamily: 'monospace',
});

const inputStyle: React.CSSProperties = {
  background: '#15171c',
  color: '#fff',
  border: '1px solid #2a2f3a',
  borderRadius: 3,
  padding: '4px 8px',
  fontSize: 11,
  fontFamily: 'monospace',
  width: 180,
};

// Phase 3: folded into the unified window engine — CONTENT only. WindowHost
// owns the ToolWindow chrome + open/close (frozen geometry key
// `toolwindow_netobs-packet-log`).
export const NetobsPacketLogPanelContent: React.FC = () => {
  const [events, setEvents] = useState<TrafficEvent[]>([]);
  const [outboundStats, setOutboundStats] = useState<Map<string, MessageTypeStats>>(new Map());
  const [inboundStats, setInboundStats] = useState<Map<string, MessageTypeStats>>(new Map());
  const [paused, setPaused] = useState(false);
  const [dirFilter, setDirFilter] = useState<DirectionFilter>('all');
  const [typeFilter, setTypeFilter] = useState('');
  const [expandedSeq, setExpandedSeq] = useState<number | null>(null);
  const [damageCounters, setDamageCounters] = useState<{
    bufferedCount: number;
    landedDrainCount: number;
    ttlDrainCount: number;
    pendingSize: number;
  } | null>(null);

  useEffect(() => {
    if (paused) return;
    const tick = () => {
      const data = readTracer();
      if (!data) return;
      setEvents(data.events.slice(-MAX_VISIBLE_ROWS).reverse());
      setOutboundStats(new Map(data.outboundStats));
      setInboundStats(new Map(data.inboundStats));

      const dp = (window as any).__damagePresentation;
      if (dp) {
        setDamageCounters({
          bufferedCount: dp.bufferedCount ?? 0,
          landedDrainCount: dp.landedDrainCount ?? 0,
          ttlDrainCount: dp.ttlDrainCount ?? 0,
          pendingSize: typeof dp.getPendingSize === 'function' ? dp.getPendingSize() : 0,
        });
      }
    };
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [paused]);

  const filtered = useMemo(() => {
    return events.filter((ev) => {
      if (dirFilter === 'in' && ev.direction !== 'server->client') return false;
      if (dirFilter === 'out' && ev.direction !== 'client->server') return false;
      if (typeFilter && !ev.type.toLowerCase().includes(typeFilter.toLowerCase())) return false;
      return true;
    });
  }, [events, dirFilter, typeFilter]);

  const totals = useMemo(() => {
    let bytesIn = 0, bytesOut = 0, msgsIn = 0, msgsOut = 0;
    for (const ev of events) {
      if (ev.direction === 'server->client') { bytesIn += ev.bytes; msgsIn += 1; }
      else                                    { bytesOut += ev.bytes; msgsOut += 1; }
    }
    return { bytesIn, bytesOut, msgsIn, msgsOut };
  }, [events]);

  const tracerBound = readTracer() != null;

  return (
      <div data-testid="netobs-packet-log-panel" style={{ padding: 8 }}>
        {!tracerBound && (
          <div style={{ color: '#888', padding: 12, fontStyle: 'italic', fontSize: 11 }}>
            Waiting for NetworkManager (window.nm).
          </div>
        )}

        {damageCounters && (
          <div style={{
            fontSize: 10,
            fontFamily: 'monospace',
            color: '#aaa',
            marginBottom: 8,
            padding: '4px 6px',
            background: 'rgba(255,255,255,0.03)',
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.06)',
          }}>
            <span style={{ color: '#7af' }}>FCT-buffer</span>{' '}
            buffered: <span style={{ color: '#fff' }}>{damageCounters.bufferedCount}</span>{' · '}
            drained: <span style={{ color: '#51cf66' }}>{damageCounters.landedDrainCount}</span>{' · '}
            ttl: <span style={{ color: '#ffd23f' }}>{damageCounters.ttlDrainCount}</span>{' · '}
            pending: <span style={{ color: '#ff5f5f' }}>{damageCounters.pendingSize}</span>
          </div>
        )}

        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          <span style={chipStyle(dirFilter === 'all')} onClick={() => setDirFilter('all')}>All</span>
          <span style={chipStyle(dirFilter === 'out')} onClick={() => setDirFilter('out')}>
            <span style={{ color: NETOBS_COLORS.DIR_OUT }}>▲</span> Out
          </span>
          <span style={chipStyle(dirFilter === 'in')} onClick={() => setDirFilter('in')}>
            <span style={{ color: NETOBS_COLORS.DIR_IN }}>▼</span> In
          </span>
          <input
            type="text"
            placeholder="filter by type…"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={inputStyle}
          />
          <span style={chipStyle(paused)} onClick={() => setPaused((p) => !p)}>
            {paused ? '▶ Resume' : '❚❚ Pause'}
          </span>
          <span style={{ color: '#666', marginLeft: 'auto', fontSize: 11 }}>
            {filtered.length}/{events.length}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 14, marginBottom: 8, fontSize: 11, color: '#aaa' }}>
          <span>OUT: <span style={{ color: '#fff' }}>{totals.msgsOut}</span> · {fmtBytes(totals.bytesOut)}</span>
          <span>IN: <span style={{ color: '#fff' }}>{totals.msgsIn}</span> · {fmtBytes(totals.bytesIn)}</span>
        </div>

        <div
          style={{
            maxHeight: 200,
            overflowY: 'auto',
            border: '1px solid #2a2f3a',
            borderRadius: 3,
            background: '#0d0f15',
          }}
        >
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 100 }}>time</th>
                <th style={{ ...thStyle, width: 26 }}></th>
                <th style={thStyle}>type</th>
                <th style={{ ...thStyle, width: 60, textAlign: 'right' }}>bytes</th>
                <th style={{ ...thStyle, width: 50, textAlign: 'right' }}>seq</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((ev) => {
                const isOut = ev.direction === 'client->server';
                const isExpanded = expandedSeq === ev.seq;
                const stats = isOut
                  ? outboundStats.get(ev.type)
                  : inboundStats.get(ev.type);
                return (
                  <React.Fragment key={ev.seq}>
                    <tr
                      onClick={() => setExpandedSeq(isExpanded ? null : ev.seq)}
                      style={{ cursor: 'pointer', background: isExpanded ? '#1a2030' : undefined }}
                    >
                      <td style={{ ...tdStyle, color: '#888' }}>{fmtTime(ev.timestamp)}</td>
                      <td style={{ ...tdStyle, color: isOut ? NETOBS_COLORS.DIR_OUT : NETOBS_COLORS.DIR_IN, textAlign: 'center' }}>
                        {isOut ? '▲' : '▼'}
                      </td>
                      <td style={{ ...tdStyle, color: ev.type.startsWith('[lag]') ? '#ff5f5f' : '#ddd', fontWeight: ev.type.startsWith('[lag]') ? 600 : 400 }}>
                        {ev.type}
                      </td>
                      <td style={{ ...tdStyle, color: '#aaa', textAlign: 'right' }}>{fmtBytes(ev.bytes)}</td>
                      <td style={{ ...tdStyle, color: '#555', textAlign: 'right' }}>{ev.seq}</td>
                    </tr>
                    {isExpanded && stats && (
                      <tr>
                        <td colSpan={5} style={{ padding: '6px 14px', background: '#15171c', borderBottom: '1px solid #1a1d24' }}>
                          <div style={{ fontSize: 10, color: '#7af', marginBottom: 4 }}>
                            TYPE STATS — {ev.type}
                          </div>
                          <div style={{ display: 'flex', gap: 14, fontSize: 11, color: '#aaa', flexWrap: 'wrap' }}>
                            <span>count: <span style={{ color: '#fff' }}>{stats.count}</span></span>
                            <span>total: <span style={{ color: '#fff' }}>{fmtBytes(stats.totalBytes)}</span></span>
                            <span>avg: <span style={{ color: '#fff' }}>{stats.avgBytes.toFixed(0)}B</span></span>
                            {stats.lastAt && <span>last: <span style={{ color: '#fff' }}>{fmtTime(stats.lastAt)}</span></span>}
                          </div>
                          <div style={{ marginTop: 4, fontSize: 10, color: '#666', fontStyle: 'italic' }}>
                            (NetworkTracer keeps metadata only — payloads not retained.)
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filtered.length === 0 && tracerBound && (
                <tr>
                  <td colSpan={5} style={{ ...tdStyle, color: '#666', textAlign: 'center', padding: 16, fontSize: 11 }}>
                    No matching packets. Adjust filters or wait for traffic.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
  );
};
